import { useQuery } from '@tanstack/react-query';
import { VehicleIllustration } from '../../../components/VehicleIllustration';
import { Link } from 'react-router-dom';
import { Car, Plus, Star } from 'lucide-react';
import { Badge, Button, Card, EmptyState, ErrorState, Skeleton } from '@tcrd/ui';
import { Page } from '../../../components/Page';
import { api, errorMessage } from '../../../lib/api';
import { STATUS_LABEL, type VehicleDto } from './types';

export function VehiclesPage() {
  const q = useQuery({ queryKey: ['vehicles'], queryFn: () => api<VehicleDto[]>('/vehicles') });
  return (
    <Page
      title="Mis vehículos"
      back="/driver/more"
      onRefresh={() => q.refetch()}
      actions={
        <Link to="/driver/vehicles/new" aria-label="Agregar vehículo" className="tap flex items-center justify-center px-2">
          <Plus className="size-6" />
        </Link>
      }
    >
      {q.isLoading && <Skeleton className="h-32" />}
      {q.error && <ErrorState message={errorMessage(q.error)} onRetry={() => q.refetch()} />}
      {q.data?.length === 0 && (
        <EmptyState
          icon={<Car className="size-6" />}
          title="Aún no registras vehículos"
          description="Agrega tu vehículo con fotos para que los clientes lo reconozcan."
          action={<Link to="/driver/vehicles/new"><Button>Agregar vehículo</Button></Link>}
        />
      )}
      <div className="flex flex-col gap-3">
        {q.data?.map((v) => {
          const main = v.photos.find((p) => p.kind === 'MAIN') ?? v.photos[0];
          return (
            <Link key={v.id} to={`/driver/vehicles/${v.id}`}>
              <Card className="overflow-hidden flex">
                <div className="w-32 shrink-0 bg-brand-gray dark:bg-white/5 flex items-center justify-center">
                  {main ? <img src={main.url} alt="" className="h-full w-full object-cover" /> : <VehicleIllustration type={v.type} color={v.color} className="w-full h-auto p-1" />}
                </div>
                <div className="p-4 flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold">{v.make} {v.model}</h3>
                    {v.isPrimary && <Badge tone="dark"><Star className="size-3" aria-hidden /> Principal</Badge>}
                  </div>
                  <p className="text-sm text-[var(--text-muted)]">{v.year} · {v.color} · {v.plate}</p>
                  <div className="mt-2 flex gap-2 flex-wrap">
                    <Badge tone={v.status === 'ACTIVE' ? 'success' : v.status === 'MAINTENANCE' ? 'warning' : 'neutral'}>{STATUS_LABEL[v.status]}</Badge>
                    <Badge>{v.passengerCapacity} pasajeros</Badge>
                  </div>
                </div>
              </Card>
            </Link>
          );
        })}
      </div>
    </Page>
  );
}
